import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { getClient, updateClient } from "../lib/api";
import { errorMessage } from "../lib/errorMessage";
import { DateInput } from "./DateInput";
import { ALL_COUNTRY_CHOICES, countryOptionLabel } from "../lib/countries";
import { Plus, X } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import { AdminOnlyNote, NoClientNote } from "./review_queue/AnswerNotes";

/**
 * v0.4.37 CTA1 (operator sign-off 2026-06-04, ALL DEFAULTS).
 *
 * ADMIN-only inline CTA inside the SELLER_VAT_AT_ARRIVAL_MISSING
 * review group. Opens a focused dialog pre-filled with a blank
 * LOCAL registration row. On save, PATCH /api/clients/{id} with
 * the appended `company_vat_numbers` array. After success the
 * dialog stays open with a "Re-run calc" prompt so the operator
 * can drop back to Step 3.
 *
 * Reuses the same shape as `ClientEditForm.jsx` registration row
 * (cc / vat_number / type / effective_from). Minimal — no
 * VIES-format validation. v0.6.12 A5-27: the country is picked from
 * lib/countries.js and the effective-from date uses DateInput (it
 * still stores ISO); leaving it blank is still allowed (the backend
 * uses the day the calc was run when the field is blank).
 */
export const AddRegistrationCta = ({ clientId }) => {
  const { admin } = useAuth();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  // A2-581 lead 8: a card whose only answer is this one was a dead end for
  // anyone else; it now says who can answer it.
  if (!clientId) return <NoClientNote />;
  if (admin?.role !== "ADMIN") {
    return <AdminOnlyNote testid="cta-add-vat-registration-admin-only"
      label={t("ui.add_registration.title", { defaultValue: "Add VAT registration" })} />;
  }
  return (
    <>
      <button
        type="button"
        data-testid="cta-add-vat-registration"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 mt-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-[10px] px-3 py-2 rounded-full shadow-lg shadow-emerald-500/20"
      >
        <Plus size={11} weight="bold" />
        {t("ui.add_registration.title", { defaultValue: "Add VAT registration" })}
      </button>
      {open && <AddRegDialog clientId={clientId} onClose={() => setOpen(false)} />}
    </>
  );
};

const AddRegDialog = ({ clientId, onClose }) => {
  const { t } = useTranslation();
  const [cc, setCc] = useState("");
  const [vatNumber, setVatNumber] = useState("");
  const [effectiveFrom, setEffectiveFrom] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);

  const save = async () => {
    setBusy(true); setError(null);
    try {
      const client = await getClient(clientId);
      const next = [
        ...(client.company_vat_numbers || []),
        {
          country_code: cc.toUpperCase(),
          vat_number: vatNumber.trim(),
          registration_type: "LOCAL",
          registration_date: effectiveFrom || undefined,
        },
      ];
      await updateClient(clientId, { company_vat_numbers: next });
      setSaved(true);
    } catch (e) {
      setError(errorMessage(e));
    } finally { setBusy(false); }
  };

  return (
    <div
      data-testid="add-vat-registration-dialog"
      className="fixed inset-0 z-50 flex items-center justify-center bg-white/90 dark:bg-slate-900/90 backdrop-blur"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-slate-900 border border-emerald-700/60 rounded-xl max-w-md w-full p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="tabular-nums text-sm tracking-normal text-emerald-700 dark:text-emerald-300 font-bold">
            {t("ui.add_registration.title", { defaultValue: "Add VAT registration" })}
          </div>
          <button
            type="button"
            data-testid="add-vat-registration-close"
            onClick={onClose}
            className="text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-100"
          ><X size={14} weight="bold" /></button>
        </div>

        {!saved ? (
          <div className="space-y-3 tabular-nums text-xs">
            {/* v0.6.12 A5-27 — a country is picked, not typed. */}
            <FormField label={t("ui.add_registration.country", { defaultValue: "Country" })}>
              <select
                data-testid="add-vat-cc"
                value={cc}
                onChange={(e) => setCc(e.target.value)}
                className="w-full bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 px-3 py-2 rounded-xl text-slate-900 dark:text-slate-50"
              >
                <option value="">—</option>
                {ALL_COUNTRY_CHOICES.map((c) => (
                  <option key={c} value={c}>{countryOptionLabel(c)}</option>
                ))}
              </select>
            </FormField>
            <FormField label={t("ui.add_registration.vat_number", { defaultValue: "VAT number" })}>
              <input
                data-testid="add-vat-number"
                value={vatNumber}
                onChange={(e) => setVatNumber(e.target.value)}
                className="w-full bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 px-3 py-2 rounded-xl text-slate-900 dark:text-slate-50"
              />
            </FormField>
            <FormField label={t("ui.add_registration.effective_from", { defaultValue: "Effective from (optional)" })}>
              <DateInput
                testid="add-vat-effective"
                allowClear
                value={effectiveFrom}
                onChange={(iso) => setEffectiveFrom(iso)}
                className="w-full bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 px-3 py-2 rounded-xl text-slate-900 dark:text-slate-50"
              />
            </FormField>
            <FormField label={t("ui.add_registration.type", { defaultValue: "Type" })}>
              <div data-testid="add-vat-type-fixed" className="text-slate-500 dark:text-slate-400">
                {t("ui.add_registration.type_local_fixed", { defaultValue: "LOCAL (fixed)" })}
              </div>
            </FormField>
            {error && (
              <div data-testid="add-vat-error" className="text-rose-700 dark:text-rose-300 tabular-nums text-[11px] bg-rose-500/10 border border-rose-200 dark:border-rose-800 rounded p-2">
                {error}
              </div>
            )}
            <button
              type="button"
              data-testid="add-vat-save"
              disabled={busy || !cc || !vatNumber}
              onClick={save}
              className={`w-full bg-emerald-600 text-white font-semibold py-3 rounded-xl shadow-lg shadow-emerald-500/20 ${
                (busy || !cc || !vatNumber) ? "opacity-50 cursor-not-allowed" : "hover:bg-emerald-700"
              }`}
            >
              {busy ? t("ui.add_registration.saving", { defaultValue: "Saving…" }) : t("ui.add_registration.save", { defaultValue: "Save registration" })}
            </button>
          </div>
        ) : (
          <div data-testid="add-vat-saved" className="space-y-3 tabular-nums text-xs text-emerald-700 dark:text-emerald-300">
            {t("ui.add_registration.saved", { defaultValue: "Registration saved. Re-run the calc from Step 3 to apply the new registration to FC_TRANSFER routing." })}
            {/* v0.6.8 fix — was a plain <a href>; see
                RegistrationsWarning.jsx for why that wipes the
                in-progress workflow session (here, at minimum it
                needlessly discards the operator's client/run/period
                selection on Step 3, forcing them to re-pick it). */}
            <Link
              to="/workflow/calculate"
              data-testid="add-vat-goto-calc"
              className="block text-center bg-teal-600 text-white font-semibold py-3 rounded-xl shadow-lg shadow-teal-500/10 hover:bg-teal-700 mt-3"
            >
              {t("ui.add_registration.goto_step3", { defaultValue: "Go to Step 3 →" })}
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

const FormField = ({ label, children }) => (
  <label className="block">
    <div className="text-[10px] tracking-normal text-slate-500 dark:text-slate-400 mb-1">
      {label}
    </div>
    {children}
  </label>
);
